/* The homepage buy box (sections/st-buy.liquid), "count and a live receipt" (2026-10-09).
   - The platform pills switch the product in place: the receipt's title and photo, the
     not-affiliated line and the packs used for the price, Add to cart and Buy it now. The count
     stays, so the multibuy saving stays with whichever platform is chosen.
   - The count: the number and its -/+ take any count to 9,999; the quick picks jump to each pack
     size and show its saving against single cards.
   - Each count is priced as the cheapest mix of the chosen product's available packs (ties go to
     fewer lines). The receipt lists that mix line by line, what the same cards cost one at a time,
     the saving, the total and the price a card. The total counts to its new value over
     --gv-dur-slow (none under reduced motion).
   - Add to cart posts the mix to /cart/add.js, then announces a CartLinesUpdateEvent so Horizon's
     cart drawer opens and redraws as for its own add. Buy it now is a cart permalink of the mix.
   - When a larger count costs less than the chosen one (4 single cards cost more than the 5-pack;
     7 cards more than the 10-pack), the receipt says so and offers to switch: the smallest such
     count, from one table of cheapest totals up to the chosen count plus the largest pack.
   - The first real choice marks the box [data-st-used], which arms the phone sticky bar, and the
     bar's label follows the product and the count.
   - Every count announces its mix as an st:pack-mix event, as assets/st-pack.js does, so
     assets/st-track.js publishes st_pack_changed from the homepage too (a platform switch counts).
   Pricing follows assets/st-pack.js (the product page's slider); kept separate because that one
   drives Horizon's product details and this one owns its markup. Money and the count-up come from
   assets/st-money.js, shared with it. */
import { money, countUp } from '@theme/st-money';
import '@theme/st-permalink'; // Buy it now carries the cart's attributes to checkout

if (!customElements.get('st-buy')) {
  customElements.define(
    'st-buy',
    class extends HTMLElement {
      connectedCallback() {
        try {
          this.data = JSON.parse(this.querySelector('[data-st-buy]')?.textContent || 'null');
        } catch (e) {
          this.data = null;
        }
        if (!this.data?.products?.length) return;
        this.count = 1;
        this.$ = (sel) => this.querySelector(sel);
        this.number = this.$('.st-buy__n');
        this.noun = this.$('.st-buy__noun');
        this.quick = this.$('[data-st-quick]');
        this.form = this.$('[data-st-form]');
        this.now = this.$('[data-st-now]');
        this.$('[data-st-count]').hidden = false;

        this.querySelectorAll('[data-st-plat]').forEach((r) =>
          r.addEventListener('change', () => r.checked && this.#product(Number(r.value)))
        );
        // The number field is as wide as its digits, so the digit sits at the counter's centre.
        this.fit = () => this.number.style.setProperty('--st-dg', String(Math.max(1, String(this.number.value).length)));
        this.number.addEventListener('input', () => {
          this.fit();
          const v = Number(this.number.value);
          if (Number.isFinite(v) && v >= 1) this.#set(v, { from: 'number' });
        });
        this.number.addEventListener('change', () => this.#set(Number(this.number.value) || 1, { up: true }));
        this.querySelectorAll('[data-st-step]').forEach((b) =>
          b.addEventListener('click', () => this.#set(this.count + Number(b.dataset.stStep), {}))
        );
        this.$('[data-st-switch]')?.addEventListener('click', (e) => this.#set(Number(e.currentTarget.dataset.count), {}));
        this.quick.addEventListener('click', (e) => {
          const t = e.target.closest('[data-size]');
          if (t) this.#set(Number(t.dataset.size), {});
        });
        // Never the form's own post: with script on it carries no variant id (the pack list is in
        // <noscript>), so it would land on Shopify's missing-parameter page.
        this.form?.addEventListener('submit', (e) => {
          e.preventDefault();
          if (this.result) this.#addToCart(e.submitter || this.form.querySelector('[type="submit"]'));
        });
        const checked = this.querySelector('[data-st-plat]:checked');
        this.#product(checked ? Number(checked.value) : 0, true);
      }

      // Switch to product i: its packs and quick picks; the photo, title and line follow.
      #product(i, quiet) {
        const p = this.data.products[i];
        if (!p) return;
        // Until the visitor has chosen a count, each platform opens on its own default pack (the
        // 5-pack, Matt 2026-10-21); a count they chose is kept across platforms.
        if (!this.counted) this.count = Number(p.start) || 1;
        if (!quiet) this.setAttribute('data-st-used', '');
        this.p = p;
        this.packs = p.packs.filter((x) => x.size > 0).sort((a, b) => a.size - b.size);
        const avail = this.packs.filter((x) => x.available);
        this.sellable = avail.length ? avail : this.packs;
        this.#buildQuick();
        const title = this.$('[data-st-title]');
        if (title) title.textContent = p.title;
        const mark = this.$('[data-st-mark]');
        if (mark) mark.textContent = p.mark;
        this.querySelectorAll('[data-st-photo]').forEach((img) => img.classList.toggle('is-off', Number(img.dataset.stPhoto) !== i));
        this.#set(this.count, { quiet: true });
      }

      // The quick picks: one button per pack size, with its saving against single cards.
      #buildQuick() {
        const base = this.packs[0];
        const sizes = [...new Set(this.packs.map((x) => x.size))];
        this.quick.replaceChildren(
          ...sizes.map((size) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'st-buy__qp';
            b.dataset.size = String(size);
            const r = this.mix(size);
            let pct = 0;
            if (base && r && size > base.size) pct = Math.round(100 - (r.total * 100) / ((base.price / base.size) * size));
            const label = `${size} ${size === 1 ? this.data.singular : this.data.plural}`;
            b.setAttribute('aria-label', pct > 0 ? `${label}, ${this.data.pct.replace('[percent]', pct)}` : label);
            // The single card shows its price where the packs show their saving, so every pick has two lines.
            const second = pct > 0 ? `<small>−${pct}%</small>` : base && size === base.size ? `<small class="is-price">${money(base.price, this.data.money, this.data.currency)}</small>` : '';
            b.innerHTML = `<b>${size}</b>${second}`;
            return b;
          })
        );
      }

      // Cheapest mix of packs for n units; ties go to fewer lines.
      mix(n) {
        const packs = this.sellable;
        const cost = new Array(n + 1).fill(Infinity);
        const lines = new Array(n + 1).fill(Infinity);
        const pick = new Array(n + 1).fill(-1);
        cost[0] = 0;
        lines[0] = 0;
        for (let k = 1; k <= n; k++) {
          packs.forEach((p, j) => {
            if (p.size > k || cost[k - p.size] === Infinity) return;
            const c = cost[k - p.size] + p.price;
            const l = lines[k - p.size] + 1;
            if (c < cost[k] || (c === cost[k] && l < lines[k])) {
              cost[k] = c;
              lines[k] = l;
              pick[k] = j;
            }
          });
        }
        if (cost[n] === Infinity) return null;
        const used = new Map();
        for (let k = n; k > 0; k -= packs[pick[k]].size) {
          const p = packs[pick[k]];
          used.set(p, (used.get(p) || 0) + 1);
        }
        return { total: cost[n], items: [...used].sort((a, b) => b[0].size - a[0].size).map(([p, q]) => ({ pack: p, quantity: q })) };
      }

      #set(value, { from, quiet, up } = {}) {
        // Counts start at the smallest pack that can be bought, and a count no mix of the sellable
        // packs can make moves on to the next one that can be made, in the direction of travel (as
        // assets/st-pack.js). Until 2026-10-16 the count stayed at 1 with no mix while the single
        // card was sold out, so Add to cart had nothing to add (code review finding 3).
        const least = this.sellable?.[0]?.size || 1;
        let n = Math.max(least, Math.min(9999, Math.round(Number(value) || least)));
        let dir = !up && n < (this.count ?? n) ? -1 : 1;
        for (let k = 0; k < 100 && !this.mix(n); k++) {
          if (n <= least) dir = 1;
          n = Math.min(9999, Math.max(least, n + dir));
        }
        if (!quiet) {
          this.setAttribute('data-st-used', '');
          this.counted = true;
        }
        this.count = n;
        this.number.min = String(least);
        if (from !== 'number') this.number.value = String(n);
        this.fit();
        const less = this.$('[data-st-step="-1"]');
        if (less) less.disabled = n <= least;
        const more = this.$('[data-st-step="1"]');
        if (more) more.disabled = n >= 9999;
        const noun = n === 1 ? this.data.singular : this.data.plural;
        if (this.noun) this.noun.textContent = noun;
        this.quick.querySelectorAll('[data-size]').forEach((t) => t.setAttribute('aria-pressed', String(Number(t.dataset.size) === n)));
        const label = this.$('[data-st-sbuy-label]');
        if (label && this.p) label.textContent = `${this.p.title} · ${n} ${noun}`;
        this.result = this.mix(n);
        this.#receipt(quiet);
        const root = window.Shopify?.routes?.root || '/';
        if (this.now && this.result) this.now.href = `${root}cart/${this.result.items.map((it) => `${it.pack.id}:${it.quantity}`).join(',')}`;
        this.#preorder();
        const r = this.result;
        if (r && this.p) {
          const items = r.items.map((it) => ({ id: it.pack.id, quantity: it.quantity, price: it.pack.price, label: it.pack.label }));
          const detail = { product: String(this.p.id), title: this.p.title || '', currency: this.data.currency || '', count: n, total: r.total, items, initial: !this.announced };
          this.dispatchEvent(new CustomEvent('st:pack-mix', { bubbles: true, detail }));
          this.announced = true;
        }
      }

      // Native preorder, as on the product page (assets/st-pack.js): when the mix holds a pack at or
      // below zero stock that keeps selling (snippets/st-preorder.liquid), the button says Preorder, a
      // line says when it ships, the lines carry the Preorder property for fulfilment, and Buy it now
      // is hidden (a cart permalink cannot carry a property). Missing here until 2026-10-16.
      #preorder() {
        const d = this.data;
        const on = Boolean(this.result?.items.some((it) => it.pack.preorder));
        const btn = this.form?.querySelector('.st-buy__add');
        if (btn) {
          if (!btn.dataset.stLabel) btn.dataset.stLabel = btn.textContent.trim();
          const want = on ? d.preorderLabel || btn.dataset.stLabel : btn.dataset.stLabel;
          if (btn.textContent.trim() !== want) btn.textContent = want;
        }
        const note = this.$('[data-st-pre]');
        if (note) {
          const text = on ? this.p?.preorderNote || d.preorderDefault || '' : '';
          if (note.textContent !== text) note.textContent = text;
          note.hidden = !on;
        }
        if (this.now) this.now.hidden = on;
      }

      // The receipt: the mix line by line, one at a time, the saving, the total and the price a card.
      #receipt(quiet) {
        const r = this.result;
        const d = this.data;
        if (!r) return;
        const m = (c) => money(c, d.money, d.currency);
        const lines = this.$('[data-st-lines]');
        lines.replaceChildren(
          ...r.items.map((it) => {
            const li = document.createElement('li');
            const a = document.createElement('span');
            a.textContent = `${it.quantity} × ${it.pack.label}`;
            const b = document.createElement('span');
            b.innerHTML = m(it.pack.price * it.quantity);
            li.append(a, b);
            return li;
          })
        );
        const now = this.$('.st-pk-total__now');
        const from = Number(now.dataset.cents);
        now.dataset.cents = String(r.total);
        now.innerHTML = m(r.total);
        if (!quiet) countUp(now, from, d.money, d.currency);
        const n = this.count;
        const base = this.packs[0];
        const was = this.$('[data-st-was]');
        const saved = this.$('[data-st-saved]');
        const each = this.$('[data-st-each]');
        let show = false;
        if (base && n > base.size) {
          const singles = Math.round((base.price / base.size) * n);
          const percent = Math.round(100 - (r.total * 100) / singles);
          if (percent > 0) {
            show = true;
            this.$('[data-st-was-v]').innerHTML = m(singles);
            this.$('[data-st-saved-v]').innerHTML = d.save.replace('[amount]', m(singles - r.total)).replace('[percent]', percent);
          }
          each.innerHTML = d.unit.replace('[unit]', m(r.total / n)).replace('[noun]', d.singular);
        } else {
          each.textContent = d.for.replace('[label]', `${n} ${n === 1 ? d.singular : d.plural}`);
        }
        was.hidden = !show;
        saved.hidden = !show;
        const rq = this.$('[data-st-rq]');
        if (rq) rq.textContent = `×${new Intl.NumberFormat(document.documentElement.lang || 'en').format(n)}`;
        this.#nudge(m);
      }

      // The smallest larger count that costs less, from one table of cheapest totals.
      #nudge(m) {
        const box = this.$('[data-st-nudge]');
        if (!box) return;
        const n = this.count;
        const top = Math.max(...this.sellable.map((p) => p.size));
        const limit = Math.min(9999, n + top);
        const cost = new Array(limit + 1).fill(Infinity);
        cost[0] = 0;
        for (let k = 1; k <= limit; k++) {
          for (const p of this.sellable) {
            if (p.size <= k && cost[k - p.size] + p.price < cost[k]) cost[k] = cost[k - p.size] + p.price;
          }
        }
        let better = 0;
        for (let k = n + 1; k <= limit; k++) {
          if (cost[k] < cost[n]) {
            better = k;
            break;
          }
        }
        box.hidden = !better;
        if (!better) return;
        const d = this.data;
        // "25 cards cost less than 24: £166.00 instead of £170.20." Both totals, so the comparison reads
        // as prices (Paulius, 2026-10-12: "the text doesn't really make much sense").
        this.$('[data-st-nudge-t]').innerHTML = d.nudge
          .replace('[count]', better)
          .replace('[price]', m(cost[better]))
          .replace('[current]', n)
          .replace('[now]', m(cost[n]));
        const btn = this.$('[data-st-switch]');
        btn.textContent = d.switch.replace('[count]', better);
        btn.dataset.count = String(better);
      }

      async #addToCart(button) {
        const err = this.$('[data-st-error]');
        if (err) err.hidden = true;
        button?.setAttribute('aria-busy', 'true');
        if (button) button.disabled = true;
        const root = window.Shopify?.routes?.root || '/';
        const json = (res) => {
          if (!res.ok) throw new Error(String(res.status));
          return res.json();
        };
        try {
          const { CartLinesUpdateEvent } = await import('@shopify/events');
          const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((c) => c.dataset.sectionId);
          const d = this.data;
          const items = this.result.items.map((it) => ({
            id: it.pack.id,
            quantity: it.quantity,
            ...(it.pack.preorder && d.preorderProp ? { properties: { [d.preorderProp]: d.preorderValue || '' } } : {}),
          }));
          const added = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items, ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname } : {}) }),
          }).then(json);
          const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then(json);
          const deferred = CartLinesUpdateEvent.createPromise();
          this.form.dispatchEvent(
            new CartLinesUpdateEvent({
              action: 'add',
              context: 'product',
              lines: items.map((it) => ({ merchandiseId: String(it.id), quantity: it.quantity })),
              promise: deferred.promise,
            })
          );
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { sections: added.sections, items: cart.items, itemCount: cart.item_count, source: 'st-buy', didError: false },
          });
        } catch (e) {
          if (err) {
            err.textContent = this.data.error;
            err.hidden = false;
          }
        } finally {
          button?.removeAttribute('aria-busy');
          if (button) button.disabled = false;
        }
      }
    }
  );
}
